import { message } from 'antd';
import { isAxiosError } from 'axios';
import { put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/Contacts/Columns';

import type { Contact } from '~/types/models';
import { type ErrorResponse, isErrorResponse } from '~/types/responses';
import type { IRestSlice } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { createContactsSaga } from '~/redux/nestedRestSlice/saga';
import { createContactsSelectors } from '~/redux/nestedRestSlice/selectors';
import { watchHandleError } from '~/redux/app/saga';

import { actions } from './slice';

const call: any = Effects.call;

const { getColumns, getItem, getParams } = createContactsSelectors('');
const standardMethods = createContactsSaga('', { getColumns, getItem, getParams });

export function* watchCreate({ payload: item }: PayloadAction<Partial<Contact>>): Generator<Effect, void, Contact> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield put(app.title('Création...'));

    const newItem = yield call(Api.create, '/contacts', item);
    message.success('Contact créé avec succès.');

    yield put(replace(`/contacts/${newItem.humanId}/edit`));
  } catch (e) {
    yield put(app.title('Nouveau contact'));
    yield put(actions.setLoading(false));

    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  }
}

export function* watchDestroy({ payload: { id } }: PayloadAction<Contact>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield call(Api.destroy, '/contacts', id);

    message.success('Contact supprimé avec succès.');

    yield put(actions.index({}));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchMassDestroy({
  payload,
}: PayloadAction<IRestSlice<Contact>['filters']>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield call(Api.massDestroy, '/contacts', payload);

    message.success('Contacts supprimés avec succès.');

    const currentParams = yield select(getParams);
    yield put(actions.index(currentParams));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchUpdate({ payload: item }: PayloadAction<Contact>): Generator<Effect, void, Contact> {
  if (!item.id) {
    return;
  }

  yield put(actions.setLoading(true));
  yield put(app.title('Sauvegarde...'));
  yield put(actions.setError(null));

  try {
    yield call(Api.update, `/contacts/${item.id}`, item);
    const updatedItem = yield call(Api.retrieve, `/contacts/${item.id}`, {
      with: getWithParamString(Columns, 'single'),
    });
    yield put(actions.setItem(updatedItem));
    yield put(actions.setInitialItem(updatedItem));
    message.success('Contact mis à jour avec succès.');

    yield put(app.title(`${updatedItem.firstName ?? ''} ${updatedItem.lastName ?? ''}`));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(app.title(`${item.firstName ?? ''} ${item.lastName ?? ''}`));
    yield put(actions.setLoading(false));
  }
}

export function* watchContactsSagas(): Generator<ForkEffect, void> {
  yield takeEvery(actions.create, watchCreate);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.massDestroy, watchMassDestroy);
  yield takeEvery(actions.update, watchUpdate);

  yield call(standardMethods);
}

const contactsSagas = watchContactsSagas;

export default contactsSagas;
